import { Avatar, Icon, useToast } from "@uzverse/ui";
import { useEffect, useRef, useState } from "react";
import type { FormEvent, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import type { Locale } from "@uzverse/core";
import { ApprovalCard } from "../components/ApprovalCard.js";
import { ContextPanel } from "../components/ContextPanel.js";
import { ErrorNote, TaskChip, useErrorText } from "../components/common.js";
import { MessageModelPill } from "../components/ModelPicker.js";
import type { MessagePreference } from "../components/ModelPicker.js";
import { PausedCard, isPausedForConnection } from "../components/PausedCard.js";
import { TaskSteps } from "../components/TaskSteps.js";
import { AssistantPortrait } from "../components/AssistantPortrait.js";
import { useAssistant } from "../state/assistant.js";
import { useSession } from "../state/session.js";
import { TERMINAL_STATES } from "../state/store.js";
import { useAppState } from "../state/useAppState.js";
import { usePrefFlag } from "../state/usePref.js";
import { VoiceButton } from "../voice/VoiceButton.js";
import { useWorldT } from "../world/i18n.js";
import { applyTimeCommand, timeToast } from "../world/look/timeStore.js";
import { getWorldFocus } from "../world/store.js";
import { useVoice } from "../voice/useVoice.js";
import type { SttLanguage } from "../voice/stt-client.js";

const SPEAK_KEY = "uz.speakReplies";
const SUGGESTIONS = ["p1", "p2", "p3", "p4"] as const;
const SUGGESTION_ICONS = ["building", "tasks", "globe", "activity"] as const;

function readSpeak(): boolean {
  try {
    return globalThis.localStorage.getItem(SPEAK_KEY) === "1";
  } catch {
    return false;
  }
}

/** The user's `voice.language` setting as an STT language (explicit language = fast path). */
function sttLanguage(setting: unknown, ui: string): SttLanguage {
  if (setting === "tr-TR") return "tr";
  if (setting === "en-US") return "en";
  return ui === "en" ? "en" : "tr";
}

export function ChatPage() {
  const { t, i18n } = useTranslation("web");
  const errorText = useErrorText();
  const { client, services, user } = useSession();
  const toast = useToast();
  const tw = useWorldT();
  const { name: assistantName } = useAssistant();
  const { items, tasks, approvals, connection, error } = useAppState();
  const location = useLocation();
  const [text, setText] = useState("");
  const [preference, setPreference] = useState<MessagePreference>({});
  const endRef = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const [panelOpen, setPanelOpen] = usePrefFlag("uz.contextPanel", false);
  const [voiceLanguage, setVoiceLanguage] = useState<unknown>(null);
  const [speakReplies, setSpeakReplies] = useState(readSpeak);
  const spoken = useRef(new Set<string>(items.map((i) => i.id)));
  const ui = i18n.language === "en" ? "en" : "tr";
  // The session is the HttpOnly cookie, so the voice client sends no bearer token.
  const voice = useVoice({ token: null, language: sttLanguage(voiceLanguage, ui), uiLanguage: ui });

  useEffect(() => {
    let cancelled = false;
    services.api
      .listSettings()
      .then((list) => {
        if (!cancelled)
          setVoiceLanguage(list.find((s) => s.key === "voice.language")?.value ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [services]);

  // A prompt handed over from another page (e.g. Companies) lands in the composer.
  useEffect(() => {
    const prompt = (location.state as { prompt?: unknown } | null)?.prompt;
    if (typeof prompt === "string") {
      setText(prompt);
      input.current?.focus();
    }
  }, [location.state]);

  // Read finished assistant replies aloud (when enabled); history is never re-read.
  useEffect(() => {
    for (const item of items) {
      if (item.role !== "assistant" || item.streaming || spoken.current.has(item.id)) continue;
      spoken.current.add(item.id);
      if (speakReplies) void voice.speak(item.text).catch(() => undefined);
    }
  }, [items, speakReplies, voice]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [items, approvals]);

  // "/" jumps to the composer from anywhere that is not a text field.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing =
        el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");
      if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Grow the composer with its content, up to a few lines.
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [text]);

  function send(value: string) {
    const trimmed = value.trim();
    if (!trimmed) return;
    voice.stopSpeaking();
    // "Set the world to night" is a client-side command: it works without the assistant.
    const time = applyTimeCommand(trimmed);
    if (time !== null) {
      const { key, params } = timeToast(time);
      toast.show(String(tw(key, params)));
      setText("");
      return;
    }
    if (!client) return;
    // The pill's choice is for this one message; the next one starts on automatic again.
    // What the user is looking at in the world (a selected wall, say) goes with the command.
    client.sendChat(trimmed, getWorldFocus(), i18n.language as Locale, preference);
    setPreference({});
    setText("");
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    send(text);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send(text);
    }
  }

  function toggleSpeak() {
    const on = !speakReplies;
    setSpeakReplies(on);
    try {
      globalThis.localStorage.setItem(SPEAK_KEY, on ? "1" : "0");
    } catch {
      // Not persisted without storage.
    }
    if (!on) voice.stopSpeaking();
  }

  // A task's steps and approvals render once, under the last message that belongs to it.
  const lastIndexOfTask = new Map<string, number>();
  items.forEach((item, i) => {
    if (item.taskId) lastIndexOfTask.set(item.taskId, i);
  });
  const firstName = (user?.displayName ?? "").split(" ")[0] ?? "";

  return (
    <div className={`chat-layout${panelOpen ? " has-panel" : ""}`}>
      <section className="chat" aria-labelledby="chat-title">
        <header className="page-bar">
          <div className="row">
            <Avatar name={assistantName || "…"} assistant size={24} />
            <h1 id="chat-title">{t("chat.title")}</h1>
            <span role="status" className={`conn conn-${connection}`}>
              <span
                className={`dot ${connection === "online" ? "dot-ok" : connection === "offline" ? "dot-warn" : "dot-busy"}`}
              />
              {t(`chat.connection.${connection}`)}
            </span>
          </div>
          <button
            type="button"
            className="btn btn-quiet btn-icon"
            aria-pressed={panelOpen}
            aria-label={panelOpen ? t("nav.hideContext") : t("nav.showContext")}
            title={panelOpen ? t("nav.hideContext") : t("nav.showContext")}
            onClick={() => setPanelOpen(!panelOpen)}
          >
            <Icon name="panelRight" />
          </button>
        </header>

        <div className="log" role="log" aria-label={t("chat.log")} aria-live="polite">
          <div className="log-inner">
            {items.length === 0 && (
              <div className="hero rise">
                <AssistantPortrait />
                <h2>{t("chatUi.hello", { name: firstName })}</h2>
                <p className="muted">{t("chatUi.helloSub")}</p>
                <p className="eyebrow">{t("chatUi.suggestTitle")}</p>
                <div className="suggest-grid">
                  {SUGGESTIONS.map((key, i) => (
                    <button
                      key={key}
                      type="button"
                      className="suggest card-hover"
                      onClick={() => {
                        setText(t(`chatUi.suggest.${key}.prompt`));
                        input.current?.focus();
                      }}
                    >
                      <Icon name={SUGGESTION_ICONS[i] ?? "sparkles"} size={16} />
                      <strong>{t(`chatUi.suggest.${key}.title`)}</strong>
                      <span className="muted small">{t(`chatUi.suggest.${key}.prompt`)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {items.map((item, i) => {
              const task = item.taskId ? tasks[item.taskId] : undefined;
              const showTask =
                item.taskId !== null && lastIndexOfTask.get(item.taskId) === i && task;
              const taskApprovals = showTask
                ? Object.values(approvals).filter((a) => a.taskId === item.taskId)
                : [];
              return (
                <div key={`${item.role}-${item.id}`} className={`msg msg-${item.role} rise`}>
                  {item.role === "assistant" && (
                    <Avatar name={assistantName || "…"} assistant size={28} />
                  )}
                  <div className="msg-main">
                    <div className="bubble">
                      <span className="sr-only">
                        {item.role === "user" ? t("chat.you") : t("chat.assistant")}:{" "}
                      </span>
                      <p className="text">
                        {item.text}
                        {item.streaming && <span className="caret" aria-hidden="true" />}
                      </p>
                      {item.streaming && <span className="sr-only">{t("chat.streaming")}</span>}
                    </div>
                    {showTask && (
                      <div className="meta">
                        <TaskChip state={task.state} />
                        {task.artifacts.map((a) => (
                          <a
                            key={a.path}
                            className="link artifact"
                            href={a.url ?? "#"}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            <Icon name="file" size={14} />
                            {t("chat.artifact", { name: a.path.split("/").pop() ?? a.path })}
                          </a>
                        ))}
                      </div>
                    )}
                    {showTask && (
                      <TaskSteps task={task} running={!TERMINAL_STATES.includes(task.state)} />
                    )}
                    {showTask && isPausedForConnection(task) && (
                      <PausedCard task={task} isOwner={user?.isOwner ?? false} />
                    )}
                    {taskApprovals.map((a) => (
                      <ApprovalCard
                        key={a.id}
                        approval={a}
                        onDecide={(id, decision) => client?.respondToApproval(id, decision)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        </div>

        <div className="composer-wrap">
          <ErrorNote message={error ? errorText(error) : null} />
          <form className="composer" onSubmit={submit}>
            <label className="sr-only" htmlFor="chat-input">
              {t("chat.messageLabel")}
            </label>
            <textarea
              id="chat-input"
              ref={input}
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t("chat.placeholder")}
              autoComplete="off"
              enterKeyHint="send"
            />
            <div className="composer-bar">
              <div className="row">
                <button
                  type="button"
                  className="btn btn-quiet btn-icon"
                  disabled
                  aria-label={t("chatUi.attach")}
                  title={t("chatUi.attachSoon")}
                >
                  <Icon name="paperclip" />
                </button>
                <button
                  type="button"
                  className={`btn btn-quiet btn-icon${speakReplies ? " is-on" : ""}`}
                  aria-pressed={speakReplies}
                  aria-label={t("chat.speakReplies")}
                  title={speakReplies ? t("chatUi.readAloudOn") : t("chatUi.readAloudOff")}
                  onClick={toggleSpeak}
                >
                  <Icon name="volume" />
                </button>
                <MessageModelPill value={preference} onChange={setPreference} />
                <span className="muted small hide-narrow">{t("chatUi.hint")}</span>
              </div>
              <div className="row">
                <VoiceButton voice={voice} onTranscript={send} disabled={!client} />
                <button
                  type="submit"
                  className="btn btn-primary btn-icon send-btn"
                  disabled={!text.trim()}
                  aria-label={t("chat.send")}
                  title={t("chat.send")}
                >
                  <Icon name="send" />
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>
      {panelOpen && <ContextPanel onClose={() => setPanelOpen(false)} />}
    </div>
  );
}
